import type { CustomFieldDefinition } from '@/modules/inventory/types';
import { OPTION_FIELD_TYPES } from '@/modules/inventory/types';

type CustomFieldInputsProps = {
  definitions: CustomFieldDefinition[];
  values: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  disabled?: boolean;
};

export function CustomFieldInputs({
  definitions,
  values,
  onChange,
  disabled = false,
}: CustomFieldInputsProps) {
  if (definitions.length === 0) {
    return (
      <p className="auth-muted">
        No hay campos personalizados para esta categoría.
      </p>
    );
  }

  return (
    <div className="inventory-custom-fields">
      {definitions.map((field) => (
        <label key={field.id} className="auth-label">
          {field.label}
          {field.required ? ' *' : ''}
          <FieldControl
            field={field}
            value={values[field.key]}
            disabled={disabled}
            onChange={(value) => onChange(field.key, value)}
          />
        </label>
      ))}
    </div>
  );
}

type FieldControlProps = {
  field: CustomFieldDefinition;
  value: unknown;
  disabled: boolean;
  onChange: (value: unknown) => void;
};

function FieldControl({ field, value, disabled, onChange }: FieldControlProps) {
  if (field.type === 'boolean') {
    return (
      <input
        type="checkbox"
        checked={Boolean(value)}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
    );
  }

  if (field.type === 'textarea') {
    return (
      <textarea
        rows={3}
        value={typeof value === 'string' ? value : ''}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (field.type === 'number') {
    return (
      <input
        type="number"
        value={typeof value === 'number' ? value : ''}
        disabled={disabled}
        onChange={(e) =>
          onChange(e.target.value === '' ? '' : Number(e.target.value))
        }
      />
    );
  }

  if (field.type === 'date') {
    return (
      <input
        type="date"
        value={typeof value === 'string' ? value : ''}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (OPTION_FIELD_TYPES.includes(field.type)) {
    if (field.type === 'multiselect') {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      return (
        <select
          multiple
          value={selected}
          disabled={disabled}
          onChange={(e) => {
            const opts = Array.from(e.target.selectedOptions).map((o) => o.value);
            onChange(opts);
          }}
        >
          {field.options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    }
    return (
      <select
        value={typeof value === 'string' ? value : ''}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">— Seleccionar —</option>
        {field.options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      type="text"
      value={typeof value === 'string' ? value : ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}
